import "./CatalogoStyles.scss";
import React, { FC, useContext, useEffect, useState } from "react";
import Layout from "../../../layout/Layout";
import { useForm } from "react-hook-form";
import CarEntity, { TransmissionCar } from "../../../../../domain/entities/CarEntity";
import LoadingComponent from "../../../components/LoadingComponent/LoadingComponent";
import CarCardComponent from "../../../components/carCard/CarCardComponent";
import di from "../../../../../di/DependencyInjection";
import SearchCarsUseCase, { SearchCarsUseCaseName } from "../../../../../domain/use_cases/car/SearchCarsUseCase";
import { Dropdown, DropdownToggle, DropdownMenu, DropdownItem } from "reactstrap";
import OrderByEntity from "../../../../../domain/entities/OrderByEntity";
import FilterComponent from "./components/filterComponent/FilterComponent";
import { AiOutlineArrowLeft, AiOutlineArrowRight } from "react-icons/ai";
import Icons from "../../../assets/Icons";
import DeleteFilterComponent from "./components/deleteComponent/DeleteFilterComponent";
import { useLocation, useParams } from "react-router-dom";
import TypeVehicleContext from "../../../../../domain/providers/typeVehicle/TypeVehicleContext";
import TypeVehicleContextType from "../../../../../domain/providers/typeVehicle/TypeVehicleContextType";
import { Helmet } from "react-helmet-async";
import BrandContext from "../../../../../domain/providers/brand/BrandContext";
import BrandContextType from "../../../../../domain/providers/brand/BrandContextType";
import ColorContext from "../../../../../domain/providers/color/ColorContext";
import ColorContextType from "../../../../../domain/providers/color/ColorContextType";
import TagContext from "../../../../../domain/providers/tag/TagContext";
import TagContextType from "../../../../../domain/providers/tag/TagContextType";
import TypeOfFuelContext from "../../../../../domain/providers/typeOfFuel/TypeOfFuelContext";
import TypeOfFuelContextType from "../../../../../domain/providers/typeOfFuel/TypeOfFuelContextType";
import SeoDropdown from "./components/seoDropdown/SeoDropdown";
import {
    contentListHybridCarSeo,
    contentListHybridCarSeo2,
    contentListUsedTrucksSeo,
    contentListUsedTrucksSeo2,
    contentListUsedCoupesSeo,
    contentListHatchbackCarSeo,
    contentListHatchbackCarSeo2,
    contentListSedanCarSeo,
    contentListSedanCarSeo2,
    contentListUsedCoupesSeo2,
    contentListUsedMotorcyclesSeo,
    contentListUsedMotorcyclesSeo2,
} from "./constants";
import BannerYourCar from "./components/banner/BannerYourCar";
import { aplicarFiltroFijo, aplicarTiposExcluidos, etiquetasFiltroFijo } from "./catalogos";
import BannerInDrive from "./components/banner/BannerInDrive";
import { CatalogoConfig } from "./catalogos";
import { useNavigate } from "react-router-dom";
import { Financing } from "../../../components/financing/financing";
import { SkeletonCard } from "../../../components/skeletonCard/skeletonCard";
import OrangeFestModal from "../../../components/OrangeFestModal/OrangeFestModal";
import FilterComponentMobile from "./components/filterComponent/FilterComponentMobile";
import { useScrollReveal } from "../../home/useScrollReveal";
import { useScrollRestoration } from "../../../hooks/useScrollRestoration";
import { useTrackOnce } from "../../../hooks/useTrackOnce";
import { trackEvent } from "../../../../../lib/analytics";
import { IS_SCREENS_MODE } from "../../../../../config/appMode";
import {
    contarFiltrosActivos,
    ordenDeFormulario,
    tiposVehiculoDeFormulario,
    trackCatalogoBuscado,
} from "../../../../../lib/analyticsCatalogo";

const orderingOptions: OrderByEntity[] = [
    {
        label: "Relevancia",
        value: {
            keyname: "relevance",
            desc: undefined,
            warranty: undefined,
        },
    },
    {
        label: "Mayor precio",
        value: {
            keyname: "price",
            desc: true,
            warranty: undefined,
        },
    },
    {
        label: "Menor precio",
        value: {
            keyname: "price",
            desc: false,
            warranty: undefined,
        },
    },
    {
        label: "Garantía",
        value: {
            keyname: "warranty",
            desc: undefined,
            warranty: true,
        },
    },
];

// Clave estable y serializable para persistir "Ordenar por" en la URL.
//   Relevancia -> "relevance"   | Mayor precio -> "price-desc"
//   Menor precio -> "price-asc" | Garantía     -> "warranty"
const orderByToParam = (o?: OrderByEntity | null): string => {
    if (!o?.value) return "";
    const { keyname, desc } = o.value;
    if (desc === true) return `${keyname}-desc`;
    if (desc === false) return `${keyname}-asc`;
    return keyname;
};

/**
 * Catálogo de vehículos. Una sola página sirve /compra-tu-carro, /compra-tu-moto,
 * /compra-tu-camion y /compra-tu-van: lo que cambia entre ellas vive en `config`.
 */
const CatalogoPage: FC<{ config: CatalogoConfig }> = ({ config }) => {
    const navigate = useNavigate();
    const formFunctions = useForm();
    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
        setValue,
        watch,
        getValues,
    } = formFunctions;
    const { typeVehicles } = useContext(TypeVehicleContext) as TypeVehicleContextType;
    const { typeVehicleName } = useParams();
    const { brands } = useContext(BrandContext) as BrandContextType;
    const { colors } = useContext(ColorContext) as ColorContextType;
    const { tags } = useContext(TagContext) as TagContextType;
    const { typeOfFuels } = useContext(TypeOfFuelContext) as TypeOfFuelContextType;
    const location = useLocation();
    // Use window.location.search (not location.search) so that params updated via
    // window.history.replaceState() are immediately visible — React Router's location
    // does NOT update on replaceState, which caused removed filters to reappear.
    const queryParams = new URLSearchParams(window.location.search);
    // El término del buscador se restaura de la URL una sola vez, en la primera búsqueda.
    const searchSeeded = React.useRef(false);
    const [showOrangeBanner, setShowOrangeBanner] = React.useState<boolean>(
        window.localStorage.getItem("orangeFestModal") ? false : true,
    );

    const [cars, setCars] = useState<CarEntity[] | undefined>(undefined);
    const [totalCars, setTotalCars] = useState(0);
    const [page, setPage] = useState<number>(queryParams.get("page") ? Number(queryParams.get("page")) : 1);
    const [openFilters, setOpenFilters] = useState<boolean>(document.body.clientWidth > 968);
    // Estado para Mobile (Persiste en localStorage)
    const [openFiltersMobile, setOpenFiltersMobile] = useState<boolean>(() => {
        const saved = localStorage.getItem("openFiltersMobile");
        // Por defecto false en mobile a menos que se haya guardado lo contrario
        return saved !== null ? JSON.parse(saved) : false;
    });
    const [showListOrderBy, setShowListOrderBy] = useState(false);
    const [openOrderBy, setOpenOrderBy] = useState(false);
    const [maxPages, setMaxPages] = useState<number>(1);

    const brand = brands.find((brand) => brand.name === queryParams.get("brand"));
    const model = watch("model");
    // Use raw watch values (no ?? [] fallback here) so the useEffect dependency
    // array gets a stable `undefined` reference instead of a new [] on every render,
    // which would cause an infinite re-render loop.
    const traccion = watch("traccion") as string[] | undefined;
    const year = queryParams.get("year") ? queryParams.get("year") : watch("year");
    const price = watch("price");
    const typeVehicleId = watch("type_vehcile_id") as string[] | undefined;
    const transmission = watch("type_transmission") as string[] | undefined;
    const tagId = watch("tag_id") as string[] | undefined;
    const rangeMileage = watch("km");
    const fuelId = watch("type_fuel_id") as string[] | undefined;
    const colorId = watch("color_id") as string[] | undefined;
    const plateNumber = queryParams.get("plate") ? queryParams.get("plate") : watch("plate_number");
    const orderBy = watch("orderBy");
    const selectedSedes = watch("location") as string[] | undefined;

    // Derived name arrays for SEO banner conditions (safe fallback to [] at point of use)
    const typeVehicleNames = (typeVehicleId ?? []).map(
        (id) => typeVehicles.find((tv) => String(tv.id) === String(id))?.name ?? "",
    );
    const fuelNames = (fuelId ?? []).map((id) => typeOfFuels.find((f) => String(f.id) === String(id))?.name ?? "");

    //searcher filters
    useEffect(() => {
        setPage(queryParams.get("page") ? Number(queryParams.get("page")) : 1);
        _handleSearch();
    }, [
        brand,
        model,
        year,
        typeVehicleId,
        transmission,
        tagId,
        fuelId,
        colorId,
        plateNumber,
        orderBy,
        traccion,
        selectedSedes,
        location.search,
        // Para excluir tipos hace falta la lista de tipos; al cargar, se repite la búsqueda.
        config.filtroFijo?.tiposExcluidos ? typeVehicles.length : 0,
    ]);

    useEffect(() => {
        _handleSearch();
    }, [page]);

    useEffect(() => {
        localStorage.setItem("openFiltersMobile", JSON.stringify(openFiltersMobile));
    }, [openFiltersMobile]);
    useEffect(() => {
        if (
            location.pathname === "/compra-tu-carro/camionetas-usadas/" ||
            location.pathname === "/compra-tu-carro/camionetas-usadas"
        ) {
            setValue("type_vehcile_id", ["2"]);
        }
    }, []);

    // Resolve name-based ?type_vehicle=Sedan URL param once typeVehicles context loads.
    // On fresh page loads _handleSearch runs before the context is ready, so the name→ID
    // lookup returns nothing. This effect retries when the context array becomes available.
    useEffect(() => {
        if (config.bodyTypeFijo) return;
        if (!typeVehicles.length) return;
        const params = new URLSearchParams(window.location.search);
        const typeVehicleParam = params.get("type_vehicle");
        if (!typeVehicleParam) return;
        const parts = typeVehicleParam.split("-").filter(Boolean);
        const allNumeric = parts.every((p) => /^\d+$/.test(p));
        if (allNumeric) return; // Numeric IDs are handled directly, no lookup needed
        const found = typeVehicles.find((tv) => tv.name === typeVehicleParam);
        if (!found) return;
        const currentTypeIds = (watch("type_vehcile_id") as string[] | undefined) ?? [];
        if (!currentTypeIds.includes(String(found.id))) {
            setValue("type_vehcile_id", [String(found.id)]);
        }
    }, [typeVehicles]);

    // ── Persistencia de "Ordenar por" en la URL ──────────────────────────────
    // Al montar, restaurar el orden seleccionado desde la URL (?orderBy=...).
    useEffect(() => {
        const param = new URLSearchParams(window.location.search).get("orderBy");
        if (!param) return;
        const match = orderingOptions.find((o) => orderByToParam(o) === param);
        if (match && !watch("orderBy")) {
            setValue("orderBy", match);
        }
    }, []);

    // Reflejar el orden seleccionado en la URL. Se omite el primer render para no
    // borrar el parámetro antes de restaurarlo (efecto de arriba). Usa replaceState
    // igual que el resto de filtros, así no re-dispara la búsqueda por location.search.
    const orderBySynced = React.useRef(false);
    useEffect(() => {
        if (!orderBySynced.current) {
            orderBySynced.current = true;
            return;
        }
        const params = new URLSearchParams(window.location.search);
        const param = orderByToParam(orderBy);
        if (param && param !== "relevance") {
            params.set("orderBy", param);
        } else {
            params.delete("orderBy");
        }
        const query = params.toString();
        window.history.replaceState({}, "", `${window.location.pathname}${query ? `?${query}` : ""}`);
    }, [orderBy]);

    // Los filtros de rango (precio y kilometraje) buscan con debounce: 600 ms después
    // del último cambio. Antes se alternaba un booleano para reiniciar el temporizador,
    // pero eso cancelaba la búsqueda en los cambios pares y el filtro no se aplicaba.
    // La firma evita repetir la búsqueda si otro efecto ya la disparó con esos rangos.
    const lastRangeSearched = React.useRef("");
    const _rangeSignature = (priceValue: any, kmValue: any) =>
        `${priceValue?.min ?? ""}|${priceValue?.max ?? ""}|${kmValue?.min ?? ""}|${kmValue?.max ?? ""}`;
    useEffect(() => {
        if (lastRangeSearched.current === _rangeSignature(price, rangeMileage)) return;
        const timer = setTimeout(() => {
            _handleSearch();
        }, 600);
        return () => clearTimeout(timer);
    }, [price?.min, price?.max, rangeMileage?.min, rangeMileage?.max]);

    // const _handleChangeTypeVehicle = () => {
    //     setValue("type_vehcile_id", typeVehicles.find((typeVehicle) => typeVehicle.name == typeVehicleName)?.id);
    // };

    // useEffect(() => {
    //     _handleChangeTypeVehicle();
    // }, [typeVehicleName]);

    const _handleSearch = async () => {
        // Sin la lista de tipos no se puede pedir "todo menos motos": se espera a que cargue.
        if (config.filtroFijo?.tiposExcluidos && !typeVehicles.length) return;
        try {
            // Seed search from URL (?search=). Igual que el resto de filtros: se siembra
            // dentro de _handleSearch para que el primer load ya aplique el término, y se
            // sincroniza el estado local del input para que se vea el texto restaurado.
            // Solo se siembra una vez: si se repitiera, al borrar el texto volvería a
            // entrar el término viejo desde la URL y el campo nunca quedaría vacío.
            if (!searchSeeded.current) {
                searchSeeded.current = true;
                const searchParam = queryParams.get("search");
                if (searchParam) {
                    setValue("search", searchParam);
                    setSearch(searchParam);
                }
            }

            // Seed year from URL
            const yearParam = queryParams.get("year");
            if (yearParam) setValue("year", yearParam);

            // Seed traccion from URL (hyphen-separated values)
            const traccionParam = queryParams.get("traccion");
            if (traccionParam && !watch("traccion")) {
                setValue("traccion", traccionParam.split("-").filter(Boolean));
            }

            // Seed plate from URL (hyphen-separated digits)
            if (queryParams.get("plate") && !watch("plate_number")) {
                setValue("plate_number", queryParams.get("plate")!.split("-").filter(Boolean));
            }

            // Seed brand from URL
            const brandQuery: any = brands.filter((item: any) => item.name == queryParams.get("brand"));
            const currentBrandId = watch("brand_id");
            const hasExistingBrandSelection = Array.isArray(currentBrandId)
                ? currentBrandId.length > 0
                : !!currentBrandId;
            if (!hasExistingBrandSelection) {
                setValue("brand_id", brandQuery[0]?.id ? brandQuery[0].id : "");
            }

            // Seed type_vehcile_id from URL.
            // New format: hyphen-separated numeric IDs  e.g. "3-6"
            // Old format (navbar links): vehicle type name  e.g. "Sedan"
            const typeVehicleParam = config.bodyTypeFijo ? null : queryParams.get("type_vehicle");
            if (typeVehicleParam) {
                const parts = typeVehicleParam.split("-").filter(Boolean);
                const allNumeric = parts.every((p) => /^\d+$/.test(p));
                let urlTypeIds: string[];
                if (allNumeric) {
                    urlTypeIds = parts;
                } else {
                    // Name-based (navbar link) — look up ID by name
                    const found = typeVehicles.find((tv) => tv.name === typeVehicleParam);
                    urlTypeIds = found ? [String(found.id)] : [];
                }
                if (urlTypeIds.length) {
                    const currentTypeIds = (watch("type_vehcile_id") as string[] | undefined) ?? [];
                    const isSame =
                        urlTypeIds.length === currentTypeIds.length &&
                        urlTypeIds.every((id) => currentTypeIds.includes(id));
                    if (!isSame) {
                        setValue("type_vehcile_id", urlTypeIds);
                    }
                }
            }

            // Seed transmission from URL (hyphen-separated values)
            const transmissionParam = queryParams.get("transmission");
            if (transmissionParam && !watch("type_transmission")) {
                setValue("type_transmission", transmissionParam.split("-").filter(Boolean));
            }

            // Seed tag from URL (hyphen-separated IDs)
            const tagParam = queryParams.get("tag");
            if (tagParam && !watch("tag_id")) {
                setValue("tag_id", tagParam.split("-").filter(Boolean));
            }

            // Seed color from URL (hyphen-separated names)
            const colorParam = queryParams.get("color");
            if (colorParam && !watch("color_id")) {
                setValue("color_id", colorParam.split("-").filter(Boolean));
            }

            // Seed fuel from URL (hyphen-separated IDs)
            const fuelParam = queryParams.get("typeOfFuels");
            if (fuelParam && !watch("type_fuel_id")) {
                setValue("type_fuel_id", fuelParam.split("-").filter(Boolean));
            }

            // Seed price from URL
            const minPrice = queryParams.get("min_price");
            const maxPrice = queryParams.get("max_price");
            if ((minPrice || maxPrice) && !watch("price")) {
                if (minPrice) setValue("price.min", Number(minPrice));
                if (maxPrice) setValue("price.max", Number(maxPrice));
            }

            // Seed km from URL
            const kmMinParam = queryParams.get("km_min");
            const kmMaxParam = queryParams.get("km_max");
            if ((kmMinParam || kmMaxParam) && !watch("km")) {
                if (kmMinParam) setValue("km.min", Number(kmMinParam));
                if (kmMaxParam) setValue("km.max", Number(kmMaxParam));
            }

            // Seed location from URL (hyphen-separated IDs)
            const locationParam = queryParams.get("location");
            if (locationParam && !watch("location")) {
                setValue("location", locationParam.split("-").filter(Boolean));
            }

            const data = formFunctions.getValues();
            lastRangeSearched.current = _rangeSignature(data.price, data.km);

            window.scrollTo({
                top: 0,
                behavior: "auto",
            });
            setCars(undefined);
            const { year: searchYear, price: searchPrice } = aplicarFiltroFijo(
                config.filtroFijo,
                data.year,
                data.price,
            );
            const response = await di.get<SearchCarsUseCase>(SearchCarsUseCaseName).call(
                page,
                data.search,
                data.brand_id,
                Array.isArray(data.model) ? data.model : data.model?.id,
                searchYear,
                searchPrice,
                // En los catálogos acotados (motos, camiones, vans) el tipo no lo
                // elige el usuario: es el catálogo entero.
                config.bodyTypeFijo ??
                    aplicarTiposExcluidos(
                        config.filtroFijo,
                        data.type_vehcile_id,
                        typeVehicles.map((tv) => String(tv.id)),
                    ),
                data.type_transmission,
                data.tag_id,
                data.km,
                data.type_fuel_id,
                data.color_id,
                data.plate_number,
                data.orderBy,
                data.claims,
                data.amount_claims,
                data.warranty,
                data.type_warranty,
                data.types_claims,
                data.traccion,
                data.location,
            );
            const repsonseCars = response.cars.sort((a, b) => {
                if (a.tag?.name === "Vehículo por ingresar" && b.tag?.name !== "Vehículo por ingresar") {
                    return 1;
                }
                if (a.tag?.name !== "Vehículo por ingresar" && b.tag?.name === "Vehículo por ingresar") {
                    return -1;
                }
                if (
                    a.tag?.name === "Vehículo por ingresar y fuera de estándar" &&
                    b.tag?.name !== "Vehículo por ingresar y fuera de estándar"
                ) {
                    return 1;
                }
                if (
                    a.tag?.name !== "Vehículo por ingresar y fuera de estándar" &&
                    b.tag?.name === "Vehículo por ingresar y fuera de estándar"
                ) {
                    return -1;
                }
                return 0;
            });
            // @ts-ignore
            repsonseCars.splice(5, 0, "1");
            // @ts-ignore
            repsonseCars.splice(10, 0, "2");
            setCars(response.cars);
            setMaxPages(response.maxPages);
            setTotalCars(response?.count);

            // `trackCatalogoBuscado` descarta repeticiones consecutivas idénticas:
            // a este handler lo llaman varios efectos con los mismos criterios.
            trackCatalogoBuscado({
                tipoCatalogo: config.tipo,
                datos: data,
                pagina: page,
                resultados: response?.count ?? 0,
                marca: brands.find((b) => String(b.id) === String(data.brand_id))?.name,
                tiposVehiculo: tiposVehiculoDeFormulario(data.type_vehcile_id, typeVehicles),
            });
            window.scrollTo({
                top: 0,
                behavior: "auto",
            });
        } catch (error) {
            // Sin esto, un backend caído se ve idéntico a un catálogo vacío.
            trackEvent("catalogo_carga_fallida", {
                tipo_catalogo: config.tipo,
                mensaje_error: error?.message ?? "error desconocido",
            });
            setCars([]);
        }
    };

    const _handleOnSubmit = async (data: any) => {
        _handleSearch();
    };

    const _handleNextPage = async () => {
        const nextPage = page + 1;
        setPage(nextPage);
        trackEvent("catalogo_paginado", { tipo_catalogo: config.tipo, pagina: nextPage });
        queryParams.set("page", nextPage.toString());
        window.history.replaceState(null, "", "?" + queryParams.toString());
        document.querySelector(".content_app").scrollTo({
            top: 0,
            behavior: "smooth",
        });
    };

    const _handlePreviousPage = async () => {
        const previousPage = page - 1;
        setPage(previousPage);
        trackEvent("catalogo_paginado", { tipo_catalogo: config.tipo, pagina: previousPage });
        queryParams.set("page", previousPage.toString());
        window.history.replaceState(null, "", "?" + queryParams.toString());
        document.querySelector(".content_app").scrollTo({
            top: 0,
            behavior: "smooth",
        });
    };

    const _handleChangePage = async (page: number) => {
        const previousPage = page;
        setPage(previousPage);
        trackEvent("catalogo_paginado", { tipo_catalogo: config.tipo, pagina: previousPage });
        queryParams.set("page", previousPage.toString());
        window.history.replaceState(null, "", "?" + queryParams.toString());
        document.querySelector(".content_app").scrollTo({
            top: 0,
            behavior: "smooth",
        });
    };

    const _handleClearFilters = async () => {
        // Antes del reset, o el conteo siempre sería 0.
        trackEvent("catalogo_filtros_limpiados", {
            tipo_catalogo: config.tipo,
            filtros_activos: contarFiltrosActivos(getValues()),
        });
        reset();
        setSearch("");
        setPage(1);
        navigate(`${config.ruta}/`);
    };

    const _handlePickOrderBy = (orderByValue: OrderByEntity) => {
        setOpenOrderBy(false);
        if (watch("orderBy") == orderByValue) return;
        trackEvent("catalogo_ordenado", { tipo_catalogo: config.tipo, orden: ordenDeFormulario(orderByValue) });
        setValue("orderBy", orderByValue);
        setPage(1);
    };

    useTrackOnce("pagina_vista", { pagina: "catalogo", tipo_catalogo: config.tipo });

    // Temporizador del buscador en un ref: guardarlo en estado dejaba la referencia vieja
    // dentro del manejador, así que nunca se alcanzaba a cancelar.
    const typingTimeout = React.useRef<ReturnType<typeof setTimeout>>();
    const [search, setSearch] = useState("");

    // ── Persistencia del término de búsqueda en la URL ────────────────────────
    // Refleja el texto del buscador en la URL (?search=). Se omite el primer render
    // para no borrar el parámetro antes de restaurarlo (seed dentro de _handleSearch).
    // Usa replaceState igual que el resto de filtros, así no re-dispara la búsqueda
    // por location.search.
    const searchSynced = React.useRef(false);
    useEffect(() => {
        if (!searchSynced.current) {
            searchSynced.current = true;
            return;
        }
        const params = new URLSearchParams(window.location.search);
        if (search) {
            params.set("search", search);
        } else {
            params.delete("search");
        }
        const query = params.toString();
        window.history.replaceState({}, "", `${window.location.pathname}${query ? `?${query}` : ""}`);
    }, [search]);

    const handleInputChange = (e) => {
        const term = e.target.value;
        setValue("search", term);
        setSearch(term);

        // Solo puede haber un temporizador vivo. Antes quedaba pendiente el de la pulsación
        // anterior y volvía a lanzar la búsqueda con el texto viejo después de borrarlo.
        if (typingTimeout.current) clearTimeout(typingTimeout.current);

        // Al vaciar el campo se busca de una vez, sin esperar el retardo.
        if (term === "") {
            _handleSearch();
            return;
        }

        typingTimeout.current = setTimeout(() => {
            _handleSearch();
        }, 1000);
    };

    useEffect(() => () => clearTimeout(typingTimeout.current), []);
    useScrollReveal([cars, openFilters]);
    // Al volver del detalle con el botón "atrás", devolver al usuario al punto del
    // listado donde estaba, no al inicio de la página.
    useScrollRestoration(cars !== undefined && cars.length > 0);

    return (
        <Layout>
            <Helmet>
                <title>{config.helmet.title}</title>
                <meta name="description" content={config.helmet.description} />
                <meta name="keywords" content={config.helmet.keywords} />
                <link rel="canonical" href={config.helmet.canonical} />
                <meta name="robots" content="index, follow" />
            </Helmet>
            <form onSubmit={handleSubmit(_handleOnSubmit)}>
                <div className="w-100 position-relative buy_your_car_page bg_gray">
                    <div className="w-100 car_search bg_search py-3">
                        <div className="container d-flex flex-column flex-md-row px-md-5 justify-content-between align-items-center">
                            <div className="input_search">
                                <img
                                    src="/assets/icons/search.svg"
                                    className="text_orange"
                                    alt="Buscar"
                                    title="Buscar"
                                />
                                <input
                                    type="text"
                                    placeholder="Buscar por marca, modelo, color..."
                                    value={search}
                                    onChange={(e) => handleInputChange(e)}
                                />
                            </div>
                            <div
                                className="order_by_container my-3 my-md-0 d-none d-md-block"
                                style={{ zIndex: 9999999999999999999 }}
                            >
                                <Dropdown
                                    isOpen={openOrderBy}
                                    toggle={() => (window.screen.width >= 768 ? setOpenOrderBy(!openOrderBy) : null)}
                                >
                                    <DropdownToggle caret>
                                        <span className="order-text me-3">Ordenar por:</span>
                                        <strong className="order_button hover">
                                            {orderingOptions.find(
                                                (orderItem) =>
                                                    orderItem.value?.keyname === watch("orderBy")?.value?.keyname &&
                                                    orderItem.value?.desc === watch("orderBy")?.value?.desc,
                                            )?.label ??
                                                orderingOptions?.[0]?.label ??
                                                ""}
                                        </strong>
                                    </DropdownToggle>

                                    <DropdownMenu style={{ zIndex: 100 }}>
                                        {orderingOptions.map((option, index) => (
                                            <DropdownItem
                                                key={index}
                                                active={watch("orderBy") === option}
                                                onClick={() => _handlePickOrderBy(option)}
                                            >
                                                {option.label}
                                            </DropdownItem>
                                        ))}
                                    </DropdownMenu>
                                </Dropdown>
                            </div>
                        </div>
                    </div>
                    <div className="car_list from_left_2 ">
                        <div className="row">
                            <div
                                className={`bg_white mb-md-0   ${
                                    (window.innerWidth > 968 ? openFilters : openFiltersMobile)
                                        ? "col-md-4 col-lg-3 "
                                        : "d-none "
                                }  position-relative ${window.innerWidth > 968 ? "reveal reveal-from-left " : ""}`}
                                style={{ zIndex: openFilters && window.innerWidth < 800 && 999999 }}
                            >
                                <div className="scroll-filter">
                                    <div className="form_underline mx-4 mt-4 pb-2">
                                        <p className="mt-3 title">
                                            {" "}
                                            {config.etiquetaConteo}: {totalCars ?? "0"}
                                        </p>
                                    </div>

                                    {/* Clase desktop_only para el filtro normal */}
                                    <div className="desktop_filter_container ">
                                        <FilterComponent
                                            formFunctions={formFunctions}
                                            isOpen={openFilters}
                                            setIsOpen={setOpenFilters}
                                            onChange={_handleSearch}
                                            ocultarTipoVehiculo={!!config.bodyTypeFijo}
                                            filtroFijo={config.filtroFijo}
                                        />
                                    </div>

                                    {/* Clase mobile_only para el filtro móvil */}
                                    <div className="mobile_filter_container">
                                        <FilterComponentMobile
                                            handleClearFilters={_handleClearFilters}
                                            formFunctions={formFunctions}
                                            isOpen={openFiltersMobile}
                                            setIsOpen={setOpenFiltersMobile}
                                            onChange={_handleSearch}
                                            ocultarTipoVehiculo={!!config.bodyTypeFijo}
                                            filtroFijo={config.filtroFijo}
                                        />
                                    </div>
                                </div>
                            </div>
                            {openFilters && (
                                <div
                                    className="bg-filters"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        // setOpenFilters(false);
                                    }}
                                ></div>
                            )}
                            <div className={` ${openFilters ? "col-md-8 col-lg-9" : "col-md-12"} container_cars`}>
                                {config.tipo === "indrive" && <BannerInDrive />}

                                {((typeVehicleNames.includes("Camioneta - SUV") && !fuelNames.includes("Híbrido")) ||
                                    location.pathname === "/compra-tu-carro/camionetas-usadas") && <BannerYourCar />}

                                {typeVehicleNames.includes("Coupe") && !fuelNames.includes("Híbrido") && (
                                    <BannerYourCar text="Carros Coupe Usados" />
                                )}

                                {(((fuelNames.includes("Híbrido") || typeVehicleNames.includes("Híbrido")) &&
                                    !typeVehicleNames.includes("Camioneta - SUV")) ||
                                    location.pathname === "/compra-tu-carro/carros-hibridos-colombia") && (
                                    <BannerYourCar text="Carros Híbridos Colombia" />
                                )}

                                {((typeVehicleNames.includes("Hatchback") && !fuelNames.includes("Híbrido")) ||
                                    location.pathname === "/compra-tu-carro/Hatchback") && (
                                    <BannerYourCar text="Hatchback Colombia" />
                                )}

                                {((typeVehicleNames.includes("Sedan") && !fuelNames.includes("Híbrido")) ||
                                    location.pathname === "/compra-tu-carro/Sedan") && (
                                    <BannerYourCar text="Carros Sedan Usados" />
                                )}

                                <div className="d-none d-md-flex justify-content-between">
                                    <div className={`mt-1 ${openFilters && "md-d-none"}`}>
                                        <div
                                            className={`btn btn_light me-3 mt-2 ${
                                                !openFilters ? "filter-btn-visible" : "filter-btn-hidden"
                                            }`}
                                            onClick={() => setOpenFilters(true)}
                                        >
                                            <img src="/assets/icons/filter.svg" alt="filtrar" title="filtrar" />
                                            <span className="ms-2">Filtrar</span>
                                        </div>
                                    </div>
                                    <div className="flex-grow-1">
                                        <DeleteFilterComponent
                                            formFunctions={formFunctions}
                                            onChange={_handleSearch}
                                            filtrosFijos={etiquetasFiltroFijo(config.filtroFijo)}
                                        />
                                    </div>
                                    <div>
                                        <div className="btn btn_light mt-2" onClick={_handleClearFilters}>
                                            <Icons.Trash /> Limpiar filtros
                                        </div>
                                    </div>
                                </div>
                                <div className="d-block d-md-none justify-content-between">
                                    <div className="mt-1 d-flex flex-row-reverse justify-content-between">
                                        <div className="btn btn_light mt-2" onClick={() => setOpenFiltersMobile(true)}>
                                            <img src="/assets/icons/filter.svg" alt="filtrar" title="filtrar" />
                                            <span className="ms-2">Filtrar</span>
                                        </div>
                                        <div className="order_by_container my-3 d-md-block">
                                            <div className="d-flex align-items-center gap-2 ms-3">
                                                <p>Ordenar por: </p>
                                                <div
                                                    className="d-flex align-items-center gap-1 position-relative"
                                                    style={{
                                                        backgroundColor: "#666C8926",
                                                        padding: "8px 16px",
                                                        borderRadius: "8px",
                                                    }}
                                                    onClick={() => setShowListOrderBy(!showListOrderBy)}
                                                >
                                                    <p className="me-1">{orderBy?.label ?? "Relevancia"}</p>
                                                    <svg
                                                        width="12"
                                                        height="7"
                                                        viewBox="0 0 12 7"
                                                        fill="none"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                    >
                                                        <path
                                                            d="M6.0002 5.79969C5.59186 5.79969 5.18353 5.64219 4.87436 5.33303L1.07103 1.52969C0.901861 1.36053 0.901861 1.08053 1.07103 0.91136C1.24019 0.742193 1.52019 0.742193 1.68936 0.91136L5.49269 4.71469C5.7727 4.99469 6.22769 4.99469 6.5077 4.71469L10.311 0.91136C10.4802 0.742193 10.7602 0.742193 10.9294 0.91136C11.0985 1.08053 11.0985 1.36053 10.9294 1.52969L7.12603 5.33303C6.81686 5.64219 6.40853 5.79969 6.0002 5.79969Z"
                                                            fill="#666C89"
                                                            stroke="#666C89"
                                                            stroke-width="0.5"
                                                        />
                                                    </svg>
                                                    {/* List filter */}
                                                    {showListOrderBy && (
                                                        <div
                                                            style={{
                                                                position: "absolute",
                                                                top: "100%",
                                                                width: "100%",
                                                                background: "#fff",
                                                                left: 0,
                                                                zIndex: 9999,
                                                                borderBottomLeftRadius: "8px",
                                                                borderBottomRightRadius: "8px",
                                                                paddingTop: "10px",
                                                                paddingBottom: "10px",
                                                                gap: "6px",
                                                                display: "flex",
                                                                flexDirection: "column",
                                                            }}
                                                        >
                                                            {orderingOptions.map((option, index) => (
                                                                <p
                                                                    style={{
                                                                        textAlign: "center",
                                                                        zIndex: 9999,
                                                                    }}
                                                                    key={index}
                                                                    // active={watch("orderBy") === option}
                                                                    onClick={() => {
                                                                        _handlePickOrderBy(option);
                                                                        setShowListOrderBy(!showListOrderBy);
                                                                    }}
                                                                >
                                                                    {option.label}
                                                                </p>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex-grow-1">
                                        <DeleteFilterComponent
                                            formFunctions={formFunctions}
                                            onChange={_handleSearch}
                                            filtrosFijos={etiquetasFiltroFijo(config.filtroFijo)}
                                        />
                                    </div>
                                </div>
                                <div className="row">
                                    {cars == undefined &&
                                        Array.from({ length: 24 }).map((_, i) => (
                                            <>
                                                <div
                                                    className={`mb-3 ${
                                                        openFilters
                                                            ? "col-md-12 col-lg-6 col-xl-4"
                                                            : "col-md-4 col-xl-3"
                                                    }`}
                                                    key={i}
                                                >
                                                    <SkeletonCard />
                                                </div>
                                                {i === (openFilters ? 5 : 7) && (
                                                    <div className="d-flex justify-content-center col-12 mb-4 mt-3">
                                                        <Financing bgColor="#00fefe" textColor="#000" />
                                                    </div>
                                                )}
                                            </>
                                        ))}
                                    {cars != undefined && cars.length == 0 && <h5>NO HAY RESULTADOS</h5>}
                                    {cars != undefined &&
                                        cars.length > 0 &&
                                        cars.map((car, index) => {
                                            // @ts-ignore
                                            if (car === "1" || car === "2") {
                                                return (
                                                    <>
                                                        <div
                                                            className={`mb-3 reveal reveal-fade-up ${
                                                                openFilters
                                                                    ? "col-md-12 col-lg-6 col-xl-4"
                                                                    : "col-md-4 col-xl-3"
                                                            }`}
                                                            key={index}
                                                            style={{
                                                                display: "flex",
                                                                justifyContent: "center",
                                                            }}
                                                        >
                                                            <img
                                                                src={
                                                                    car === "1"
                                                                        ? "/assets/buyCarBanner5.png"
                                                                        : "/assets/buyCarBanner6.png"
                                                                }
                                                                className="text_orange"
                                                                alt={
                                                                    car === "1"
                                                                        ? "Garantia de 6 meses"
                                                                        : "Garantia de felicidad"
                                                                }
                                                                title={
                                                                    car === "1"
                                                                        ? "Garantia de 6 meses"
                                                                        : "Garantia de felicidad"
                                                                }
                                                                style={{
                                                                    width: "100%",
                                                                    maxWidth: "330px",
                                                                    height: "100%",
                                                                    maxHeight: "488px",
                                                                    borderTopLeftRadius: "0.5em",
                                                                    borderBottomLeftRadius: "0.5em",
                                                                    borderTopRightRadius: "0.5em",
                                                                    borderBottomRightRadius: "0.5em",
                                                                    objectFit: "cover",
                                                                    boxShadow: "0px 2px 4px 0px #efefef",
                                                                    margin: "auto",
                                                                }}
                                                            />
                                                        </div>
                                                        {index === (openFilters ? 5 : 7) && (
                                                            <div className="d-flex justify-content-center col-12 mb-4 mt-3">
                                                                <Financing bgColor="#00fefe" textColor="#000" />
                                                            </div>
                                                        )}
                                                    </>
                                                );
                                            } else {
                                                return (
                                                    <>
                                                        <div
                                                            className={`mb-3 reveal reveal-fade-up ${
                                                                openFilters
                                                                    ? "col-md-12 col-lg-6 col-xl-4"
                                                                    : "col-md-4 col-xl-3"
                                                            }`}
                                                            key={car.id}
                                                        >
                                                            <CarCardComponent
                                                                car={car}
                                                                rutaDetalle={config.rutaDetalle}
                                                                origen="catalogo"
                                                                posicion={index + 1}
                                                                pagina={page}
                                                            />
                                                        </div>
                                                        {index === (openFilters ? 5 : 7) && (
                                                            <div className="d-flex justify-content-center col-12 mb-4 mt-3">
                                                                <Financing bgColor="#00fefe" textColor="#000" />
                                                            </div>
                                                        )}
                                                    </>
                                                );
                                            }
                                        })}
                                </div>
                                {maxPages > 1 && (
                                    <>
                                        <div className="w-100 d-flex justify-content-center align-items-center my-3">
                                            <nav aria-label="Page navigation example">
                                                <ul className="pagination">
                                                    <div
                                                        className={`arrow_slider previous me-1 mt-1 ${
                                                            page <= 1 ? "disabled" : ""
                                                        }`}
                                                    >
                                                        <AiOutlineArrowLeft size={20} onClick={_handlePreviousPage} />
                                                    </div>
                                                    {/* <li className="page-item">
                                                    <a className="page-link " href="#" aria-label="Previous">
                                                        <span aria-hidden="true">&laquo;</span>
                                                    </a>
                                                </li> */}
                                                    {Array.from({ length: maxPages }, (_, index) => (
                                                        <li key={index}>
                                                            <a
                                                                // className="page-link"
                                                                className={` page-link ${
                                                                    page === index + 1 ? "active-custom" : ""
                                                                }`}
                                                                style={{
                                                                    color: page === index + 1 ? "white" : IS_SCREENS_MODE ? "#FF7300" : "#FF8000",
                                                                }}
                                                                href="#"
                                                                onClick={() => _handleChangePage(index + 1)}
                                                            >
                                                                {index + 1}
                                                            </a>
                                                        </li>
                                                    ))}

                                                    <div
                                                        className={`arrow_slider next mt-1 ms-1 ${
                                                            page >= maxPages ? "disabled" : ""
                                                        }`}
                                                    >
                                                        <AiOutlineArrowRight size={20} onClick={_handleNextPage} />
                                                    </div>
                                                </ul>
                                            </nav>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </form>
            {/* SEO camoionetas usadas */}
            {((typeVehicleNames.includes("Camioneta - SUV") && !fuelNames.includes("Híbrido")) ||
                location.pathname === "/compra-tu-carro/camionetas-usadas") && (
                <div className="my-6 pt-4 container container-drop" style={{ marginTop: "80px", marginBottom: "40px" }}>
                    <SeoDropdown
                        options={contentListUsedTrucksSeo.map((content) => {
                            return {
                                title: content.title,
                                content: content.content,
                                typeElement: content.type,
                                image: content.imageUrl,
                                alt: content.alt,
                            };
                        })}
                    />
                    <SeoDropdown
                        options={contentListUsedTrucksSeo2.map((content) => {
                            return {
                                title: content.title,
                                content: content.content,
                                typeElement: content.type,
                                image: content.imageUrl,
                                alt: content.alt,
                            };
                        })}
                    />
                </div>
            )}

            {((typeVehicleNames.includes("Coupe") && !fuelNames.includes("Híbrido")) ||
                location.pathname === "/compra-tu-carro/coupe") && (
                <div className="my-6 pt-4 container container-drop" style={{ marginTop: "80px", marginBottom: "40px" }}>
                    <div className="w-100 w-md-50">
                        <SeoDropdown
                            options={contentListUsedCoupesSeo.map((content) => {
                                return {
                                    title: content.title,
                                    content: content.content,
                                    typeElement: content.type,
                                    image: content.imageUrl,
                                    alt: content.alt,
                                };
                            })}
                        />
                    </div>
                    <div className="w-100 w-md-50">
                        <SeoDropdown
                            options={contentListUsedCoupesSeo2.map((content) => {
                                return {
                                    title: content.title,
                                    content: content.content,
                                    typeElement: content.type,
                                    image: content.imageUrl,
                                    alt: content.alt,
                                };
                            })}
                        />
                    </div>
                </div>
            )}
            {/* SEO Sedan */}
            {typeVehicleNames.includes("Sedan") && !fuelNames.includes("Híbrido") && (
                <div className="my-6 pt-4 container container-drop" style={{ marginTop: "80px", marginBottom: "40px" }}>
                    <SeoDropdown
                        options={contentListSedanCarSeo.map((content) => {
                            return {
                                title: content.title,
                                content: content.content,
                                typeElement: content.type,
                                image: content.imageUrl,
                                alt: content.alt,
                            };
                        })}
                    />
                    <SeoDropdown
                        options={contentListSedanCarSeo2.map((content) => {
                            return {
                                title: content.title,
                                content: content.content,
                                typeElement: content.type,
                                image: content.imageUrl,
                                alt: content.alt,
                            };
                        })}
                    />
                </div>
            )}
            {/* SEO - Combustible: Hibrido */}
            {(fuelNames.includes("Híbrido") || typeVehicleNames.includes("Híbrido")) &&
                !typeVehicleNames.includes("Camioneta - SUV") && (
                    <div
                        className="my-6 pt-4 container container-drop"
                        style={{
                            marginTop: "80px",
                            marginBottom: "40px",
                        }}
                    >
                        <SeoDropdown
                            options={contentListHybridCarSeo.map((content) => {
                                return {
                                    title: content.title,
                                    content: content.content,
                                    typeElement: content.type,
                                    image: content.imageUrl,
                                    alt: content.alt,
                                };
                            })}
                        />
                        <SeoDropdown
                            options={contentListHybridCarSeo2.map((content) => {
                                return {
                                    title: content.title,
                                    content: content.content,
                                    typeElement: content.type,
                                    image: content.imageUrl,
                                    alt: content.alt,
                                };
                            })}
                        />
                    </div>
                )}
            {/* SEO Hatchback */}
            {((typeVehicleNames.includes("Hatchback") && !fuelNames.includes("Híbrido")) ||
                location.pathname === "/compra-tu-carro/Hatchback") && (
                <div className="my-6 pt-4 container container-drop" style={{ marginTop: "80px", marginBottom: "40px" }}>
                    <SeoDropdown
                        options={contentListHatchbackCarSeo.map((content) => {
                            return {
                                title: content.title,
                                content: content.content,
                                typeElement: content.type,
                                image: content.imageUrl,
                                alt: content.alt,
                            };
                        })}
                    />
                    <SeoDropdown
                        options={contentListHatchbackCarSeo2.map((content) => {
                            return {
                                title: content.title,
                                content: content.content,
                                typeElement: content.type,
                                image: content.imageUrl,
                                alt: content.alt,
                            };
                        })}
                    />
                </div>
            )}

            {(config.tipo === "motos" || typeVehicleNames.includes("Motocicletas")) && (
                <div className="my-6 pt-4 container container-drop" style={{ marginTop: "80px", marginBottom: "40px" }}>
                    <SeoDropdown
                        options={contentListUsedMotorcyclesSeo.map((content) => {
                            return {
                                title: content.title,
                                content: content.content,
                                typeElement: content.type,
                                image: content.imageUrl,
                                alt: content.alt,
                            };
                        })}
                    />
                    <SeoDropdown
                        options={contentListUsedMotorcyclesSeo2.map((content) => {
                            return {
                                title: content.title,
                                content: content.content,
                                typeElement: content.type,
                                image: content.imageUrl,
                                alt: content.alt,
                            };
                        })}
                    />
                </div>
            )}
            {/* {showOrangeBanner && <OrangeFestModal setIsOpen={setShowOrangeBanner} />} */}
        </Layout>
    );
};

export default CatalogoPage;
